import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase/config';
import { sendPasswordResetEmail } from 'firebase/auth';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import AuthContext from '../contexts/AuthContext';
import SEOHead from '../components/seo/SEOHead';
import LanguageSelector from '../components/settings/LanguageSelector';
import ThemeSelector from '../components/settings/ThemeSelector';
import NotificationSettings from '../components/settings/NotificationSettings';
import PrivacySettings from '../components/settings/PrivacySettings';
import { CogIcon, BellIcon, LockClosedIcon, UserIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const Settings: React.FC = () => {
  const [user] = useAuthState(auth);
  const { logout, currentUser } = useContext(AuthContext);
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleChangePassword = async () => {
    if (!user?.email) {
      toast.error(t('settings.noEmailFound'));
      return;
    }

    try {
      await sendPasswordResetEmail(auth, user.email);
      toast.success(t('settings.passwordResetEmailSent'));
    } catch (error) {
      console.error('Error sending password reset email:', error);
      toast.error(t('settings.errorSendingPasswordReset'));
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    const confirmMessage = `${t('settings.confirmDeleteAccount')}\n\n${t('settings.deleteAccountWarning')}`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setIsDeleting(true);

      // Eliminar documento del usuario en Firestore
      const userDocRef = doc(db, 'users', user.uid);
      await deleteDoc(userDocRef);

      // Eliminar cuenta de Firebase Auth
      await user.delete();

      toast.success(t('settings.accountDeleted'));
      await logout();
    } catch (error: any) {
      console.error('Error deleting account:', error);

      // Si falla por reautenticación requerida
      if (error.code === 'auth/requires-recent-login') {
        toast.error(t('settings.recentLoginRequired'));
      } else {
        toast.error(t('settings.errorDeletingAccount'));
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <SEOHead
        title={`${t('settings.title')} | ${t('common.appName')}`}
        description={t('settings.seoDescription')}
      />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">{t('settings.title')}</h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Sidebar de navegación */}
          <div className="md:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <h2 className="text-xl font-semibold mb-4">{t('settings.categories')}</h2>
              <nav className="space-y-2">
                <a href="#account" className="flex items-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700">
                  <UserIcon className="h-5 w-5 mr-2" />
                  <span>{t('settings.account')}</span>
                </a>
                <a href="#privacy" className="flex items-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700">
                  <LockClosedIcon className="h-5 w-5 mr-2" />
                  <span>{t('settings.privacy')}</span>
                </a>
                <a href="#notifications" className="flex items-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700">
                  <BellIcon className="h-5 w-5 mr-2" />
                  <span>{t('settings.notifications')}</span>
                </a>
                <a href="#appearance" className="flex items-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700">
                  <CogIcon className="h-5 w-5 mr-2" />
                  <span>{t('settings.appearance')}</span>
                </a>
              </nav>
            </div>
          </div>

          {/* Contenido principal */}
          <div className="md:col-span-2 space-y-8">
            {/* Sección de cuenta */}
            <section id="account" className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">{t('settings.account')}</h2>

              {user && (
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <img
                      src={user.photoURL || '/default-avatar.png'}
                      alt={user.displayName || 'Usuario'}
                      className="w-16 h-16 rounded-full"
                    />
                    <div>
                      <h3 className="font-medium">{user.displayName || user.email}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <button
                      className="px-4 py-2 bg-domired-600 text-white rounded-md hover:bg-domired-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500 mb-4"
                      onClick={handleChangePassword}
                    >
                      {t('settings.changePassword')}
                    </button>

                    <button
                      className="flex items-center text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                      onClick={handleDeleteAccount}
                      disabled={isDeleting}
                    >
                      <TrashIcon className="h-5 w-5 mr-1" />
                      <span>{isDeleting ? t('common.deleting') : t('settings.deleteAccount')}</span>
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* Sección de privacidad */}
            <section id="privacy">
              {currentUser && <PrivacySettings userId={currentUser.uid} />}
            </section>

            {/* Sección de notificaciones */}
            <section id="notifications">
              <NotificationSettings />
            </section>

            {/* Sección de apariencia */}
            <section id="appearance" className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">{t('settings.appearance')}</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-3">{t('settings.theme')}</h3>
                  <ThemeSelector />
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h3 className="text-lg font-medium mb-3">{t('settings.language')}</h3>
                  <LanguageSelector />
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default Settings;
